import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, X, Loader2, AlertTriangle, ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { previewSendRun, createSendRun } from '../../../api/presend';
import { formatCount, formatMinutes } from '../format';

/**
 * The look before any bulk send (BE communications.presend).
 *
 * Shows how many people get it, who is left out and why, sample greetings and
 * the actual message, then creates a send run. Small runs start sending a few
 * a minute; large ones (or any with imported contacts) wait for another super
 * admin, after a test send, on the run page this navigates to.
 *
 * Shared by every bulk sender so they can't drift apart (CG, Monday
 * 13391839919, 1 Nov 2026).
 */
const PreSendDialog = ({ kind, params, title, noun = 'message', onClose }) => {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [testSize, setTestSize] = useState('');
  const [name, setName] = useState('');
  const [openReason, setOpenReason] = useState(null);
  const [showMessage, setShowMessage] = useState(false);

  const limit = parseInt(testSize, 10) > 0 ? parseInt(testSize, 10) : undefined;
  const plural = (n) => `${noun}${n === 1 ? '' : 's'}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await previewSendRun(kind, params, limit);
      setPreview(res.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not build the preview');
    } finally {
      setLoading(false);
    }
  }, [kind, params, limit]);

  useEffect(() => {
    // Debounced so typing a test-batch size doesn't fire a preview per keystroke.
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
  }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const res = await createSendRun(kind, params, { limit, name });
      const run = res.data;
      toast.success(
        run.status === 'pending_approval'
          ? 'Created. Send yourself a test, then another super admin approves it.'
          : 'Created. It will go out gradually, a few a minute.'
      );
      onClose();
      navigate(`/send-runs/${run.id}`);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not create the send');
    } finally {
      setCreating(false);
    }
  };

  const count = preview?.recipient_count || 0;
  const message = preview?.message_preview;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Send className="w-5 h-5 text-[#55C5D0]" /> {title}
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-5">
          {loading && !preview ? (
            <div className="flex items-center gap-2 text-gray-500 py-10 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" /> Checking who can receive it...
            </div>
          ) : preview && (
            <>
              <div className="flex flex-wrap items-end gap-6">
                <div>
                  <div className="text-4xl font-bold text-gray-900 leading-none">
                    {formatCount(count)}
                    {loading && <Loader2 className="inline w-5 h-5 ml-2 animate-spin text-gray-400" />}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {count === 1 ? 'person' : 'people'} will get it
                    {limit && preview.total_eligible > count
                      ? ` (test batch of ${formatCount(preview.total_eligible)} eligible)` : ''}
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">{formatCount(preview.excluded_total)}</span> left out
                </div>
                <div className="text-sm text-gray-500">
                  Sends about {preview.rate_per_minute} a minute
                  {count > 0 && <> (around {formatMinutes(preview.estimated_minutes)})</>}
                </div>
              </div>

              {preview.requires_approval && (
                <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
                  <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <div>
                      This needs another super admin to approve it before anything is sent. You will send
                      yourself a test first.
                    </div>
                    <ul className="list-disc ml-5 mt-1 text-amber-800">
                      {preview.review_reasons.map((r) => <li key={r}>{r}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              <label className="block text-sm">
                <span className="text-gray-700 font-medium">Send a test batch first (optional)</span>
                <span className="block text-gray-500 text-xs mb-1">
                  Only send to this many people from the list, e.g. 50, and check delivery before sending the rest.
                </span>
                <input
                  type="number" min="1" value={testSize} onChange={(e) => setTestSize(e.target.value)}
                  placeholder="Everyone eligible"
                  className="w-48 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                />
              </label>

              {preview.excluded.length > 0 && (
                <section>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Left out</h4>
                  <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                    {preview.excluded.map((row) => (
                      <li key={row.reason}>
                        <button
                          className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50"
                          onClick={() => setOpenReason(openReason === row.reason ? null : row.reason)}
                        >
                          <span className="flex items-center gap-1 text-gray-700">
                            {openReason === row.reason
                              ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            {row.label}
                          </span>
                          <span className="font-medium text-gray-900">{formatCount(row.count)}</span>
                        </button>
                        {row.breakdown?.length > 0 && (
                          <ul className="px-9 pb-2 text-xs text-gray-500 space-y-0.5">
                            {row.breakdown.map((part) => (
                              <li key={part.label} className="flex justify-between">
                                <span>{part.label}</span><span>{formatCount(part.count)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {openReason === row.reason && (
                          <ul className="px-9 pb-2 text-xs text-gray-500 space-y-0.5">
                            {row.samples.map((s, i) => (
                              <li key={`${s.contact_id}-${i}`}>
                                {s.contact_id
                                  ? <a href={`/contacts/${s.contact_id}`} target="_blank" rel="noreferrer"
                                    className="hover:underline">{s.name || '(no name)'}</a>
                                  : (s.name || '(no name)')} · {s.address || 'no address'}
                              </li>
                            ))}
                            {row.count > row.samples.length && <li>and {row.count - row.samples.length} more</li>}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {preview.sample.length > 0 && (
                <section>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    Who will get it{count > preview.sample.length ? ` (first ${preview.sample.length})` : ''}
                  </h4>
                  <div className="border border-gray-200 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                        <tr>
                          <th className="text-left px-3 py-2">Name</th>
                          <th className="text-left px-3 py-2">{preview.channel === 'sms' ? 'Mobile' : 'Email'}</th>
                          <th className="text-left px-3 py-2">Greeting</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {preview.sample.map((r) => (
                          <tr key={r.address}>
                            <td className="px-3 py-1.5 text-gray-900">{r.name}</td>
                            <td className="px-3 py-1.5 text-gray-600 break-all">{r.address}</td>
                            <td className="px-3 py-1.5 text-gray-900">{r.greeting}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {message && (
                <section>
                  <button
                    className="flex items-center gap-1 text-sm font-semibold text-gray-800"
                    onClick={() => setShowMessage(!showMessage)}
                  >
                    {showMessage ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    Preview the {noun} ({message.to})
                  </button>
                  {showMessage && (
                    <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                      {message.subject && (
                        <div className="px-3 py-2 bg-gray-50 text-xs text-gray-600">Subject: {message.subject}</div>
                      )}
                      {message.html ? (
                        <iframe
                          title="Message preview" sandbox="" srcDoc={message.html}
                          className="w-full h-[480px] bg-white"
                        />
                      ) : (
                        <pre className="px-3 py-2 text-sm whitespace-pre-wrap font-sans">{message.text}</pre>
                      )}
                    </div>
                  )}
                </section>
              )}

              {count > 0 && (
                <label className="block text-sm">
                  <span className="text-gray-700 font-medium">Name (optional)</span>
                  <input
                    value={name} onChange={(e) => setName(e.target.value)} maxLength={200}
                    placeholder="e.g. Northcote buyers, test batch"
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  />
                </label>
              )}

              {count === 0 && (
                <div className="flex gap-2 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Nobody in this list can receive it right now.
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200">
          <a href="/send-runs" className="text-sm text-[#2A9DA8] hover:underline">Past sends</a>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
              Cancel
            </button>
            <button
              onClick={create}
              disabled={loading || creating || count === 0}
              className="px-4 py-2 text-white bg-[#55C5D0] rounded-md hover:bg-[#45B5C0] disabled:opacity-50"
            >
              {creating ? 'Creating...'
                : preview?.requires_approval ? `Set up ${formatCount(count)} for approval`
                  : `Send ${formatCount(count)} ${plural(count)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreSendDialog;
